import { defineStore } from 'pinia';
import { ProductService } from '@/services/product';

interface TrainingdataState {
  /** Training data variants for the current intent, sorted by text; `false` when not loaded */
  data: any[] | false;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/trainingdata.ts` (namespace
 * `trainingdata`). In-memory only — no `vuexPersist` option (legacy module had no persistence
 * reducer entry in `src/store/index.ts`).
 *
 * No name collisions in this module: the getter `trainingdata` doesn't share a name with any
 * action/mutation, and the two legacy mutations (`setTrainingdata`, `setVariant`) don't share
 * names with any legacy action, so they convert to same-named actions with no renames. Legacy
 * actions that used to `commit('setTrainingdata', ...)` / `commit('setVariant', ...)` now call
 * `this.setTrainingdata(...)` / `this.setVariant(...)` directly.
 */
export const useTrainingdataStore = defineStore('trainingdata', {
  state: (): TrainingdataState => ({
    data: false,
  }),
  getters: {
    trainingdata: (state): any[] | false => {
      return state.data;
    },
  },
  actions: {
    async get({ uniqueBotId, name }: { uniqueBotId: string; name: string }) {
      const trainingdata: any = await ProductService.getTrainingdata(uniqueBotId, name);
      if (trainingdata && Array.isArray(trainingdata.result)) {
        this.setTrainingdata(trainingdata.result);
        return trainingdata.result;
      } else {
        return false;
      }
    },
    async addData({
      uniqueBotId,
      name,
      trainingdata,
    }: {
      uniqueBotId: string;
      name: string;
      trainingdata: string[];
    }) {
      // filter empty strings
      trainingdata = trainingdata.filter((td) => td.trim() !== '');
      let response = await ProductService.addTrainingdata(uniqueBotId, name, trainingdata);
      return response;
    },
    async deleteData({ uniqueBotId, text }: { uniqueBotId: string; text: string }) {
      await ProductService.deleteTrainingdata(uniqueBotId, text);
      return true;
    },
    async updateQualityCheck({ variant }: { variant: any }) {
      let td: any = await ProductService.updateQualityCheck(variant);
      if (td.variant) {
        this.setVariant(td.variant);
      }

      return td.variant;
    },
    async update({ variant }: { variant: any }) {
      let td: any = await ProductService.updateTrainingdata(variant);
      if (td.variant) {
        this.setVariant(td.variant);
        return td.variant;
      } else {
        return false;
      }
    },
    clear() {
      this.setTrainingdata(false);
    },
    // `trainingdata` is an array from get() or `false` from clear(); the unconditional
    // .sort matches the original JS (would throw on false — preserved behavior).
    setTrainingdata(trainingdata: any) {
      this.data = trainingdata;
      (this.data as any).sort((a: any, b: any) => {
        return a.text.localeCompare(b.text);
      });
    },
    setVariant(variant: any) {
      //console.log('VAR', variant._id, state.data);
      let index = (this.data as any[]).findIndex((td) => {
        return td._id === variant._id;
      });
      (this.data as any[]).splice(index, 1, variant);
    },
  },
});
